import type { ConsoleBrandDetail } from '@rsecure/brand-contracts/console';
import { Link } from 'react-router';
import { Badge, Json, Ltr, Notice, StatusBadge, Time } from '../../components/ui';
import { fmtBytes } from '../../lib/format';

type Rec = Record<string, unknown>;
const Missing = () => <span className="faint">not in data</span>;

export function OverviewTab({ d }: { d: ConsoleBrandDetail }) {
  const c = (d.content ?? {}) as Rec;
  const identity = (c.identity ?? {}) as Rec;
  const cls = (c.classification ?? {}) as Rec;
  const colors = (c.colors ?? {}) as {
    primary?: string | null;
    typed?: Array<{ hex: string; type: string | null }>;
  };
  const company = (c.company ?? {}) as Rec;
  const verdict = (d.identity ?? null) as Rec | null;
  const evidence =
    (verdict?.evidence as Array<{ check: string; outcome: string; detail: string }> | undefined) ??
    [];
  const localized = (identity.localizedNames ?? {}) as Record<string, string>;
  const social = (c.socialLinks as Array<{ platform: string; url: string }> | undefined) ?? [];
  const fonts =
    (c.fonts as Array<{ name: string; type: string | null; origin: string | null }> | undefined) ??
    [];
  const website = d.domains.find((x) => x.role === 'primary')?.domain;

  return (
    <div className="grid" style={{ gap: 16 }}>
      {d.contentRevision && d.contentRevision.id !== d.entity.activeRevisionId && (
        <Notice tone="warn">
          Showing content of revision #{d.contentRevision.number} ({d.contentRevision.state}) — it
          is not the active published revision.
        </Notice>
      )}
      {!d.content && (
        <Notice tone="warn">
          No normalized revision exists for this entity. Profile fields below come only from the
          entity row and its candidates; see Reviews for why normalization is blocked.
        </Notice>
      )}
      <div className="grid two">
        <section className="card" aria-labelledby="h-identity">
          <div className="card-h">
            <h2 id="h-identity">Identity</h2>
            {verdict && (
              <Badge
                tone={verdict.decision === 'verified' ? 'ok' : 'warn'}
                title="Identity verification decision recorded on the revision (not a provider claim)"
              >
                identity {String(verdict.decision)}
                {verdict.decidedBy ? ` · by ${verdict.decidedBy}` : ' · policy'}
              </Badge>
            )}
          </div>
          <div className="card-b">
            <dl className="dl">
              <dt>Canonical ID</dt>
              <dd>
                <Ltr mono>{d.entity.id}</Ltr>
              </dd>
              <dt>Canonical name</dt>
              <dd>{d.entity.canonicalName}</dd>
              <dt>Localized names</dt>
              <dd>
                {Object.keys(localized).length
                  ? Object.entries(localized).map(([l, n]) => (
                      <div key={l}>
                        <span className="muted mono">{l}</span>{' '}
                        <span lang={l} dir="auto">
                          {n}
                        </span>
                      </div>
                    ))
                  : (d.entity.nameHe ?? <Missing />)}
              </dd>
              <dt>Kind</dt>
              <dd>{d.entity.kind}</dd>
              <dt>Origin</dt>
              <dd>{d.entity.origin.replace(/_/g, ' ')}</dd>
              <dt>Verification evidence</dt>
              <dd>
                {evidence.length ? (
                  <ul className="evidence">
                    {evidence.map((ev) => (
                      <li key={ev.check + ev.detail}>
                        <Badge
                          tone={
                            ev.outcome === 'pass' ? 'ok' : ev.outcome === 'warn' ? 'warn' : 'bad'
                          }
                        >
                          {ev.check}: {ev.outcome}
                        </Badge>
                        <span>{ev.detail}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="faint">No verification verdict recorded (no revision).</span>
                )}
              </dd>
            </dl>
          </div>
        </section>
        <section className="card" aria-labelledby="h-profile">
          <div className="card-h">
            <h2 id="h-profile">Brand profile</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              see Provenance for sources
            </span>
          </div>
          <div className="card-b">
            <dl className="dl">
              <dt>Description</dt>
              <dd>{(c.description as string) ?? <Missing />}</dd>
              <dt>Category</dt>
              <dd>{(cls.category as string) ?? d.entity.category ?? <Missing />}</dd>
              <dt>Industries</dt>
              <dd>{((cls.industries as string[]) ?? []).join(', ') || <Missing />}</dd>
              <dt>Region</dt>
              <dd>
                {(cls.region as string) ?? d.entity.region}
                {cls.regionSource ? (
                  <span className="muted">
                    {' '}
                    · from {String(cls.regionSource).replace('_', ' ')}
                  </span>
                ) : null}
              </dd>
              <dt>Country / city</dt>
              <dd>{[cls.country, cls.city].filter(Boolean).join(' · ') || <Missing />}</dd>
              <dt>Company</dt>
              <dd>
                {Object.entries(company).filter(([, v]) => v != null).length ? (
                  Object.entries(company)
                    .filter(([, v]) => v != null)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(' · ')
                ) : (
                  <Missing />
                )}
              </dd>
              <dt>Website</dt>
              <dd>
                {website ? (
                  <a
                    className="link"
                    href={`https://${website}`}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <Ltr>{website}</Ltr>
                  </a>
                ) : (
                  <Missing />
                )}
              </dd>
              <dt>Links</dt>
              <dd>
                {social.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                    {social.map((s) => (
                      <a
                        key={s.url}
                        className="link"
                        href={s.url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {s.platform || 'link'}
                      </a>
                    ))}
                  </div>
                ) : (
                  <Missing />
                )}
              </dd>
              <dt>Colors</dt>
              <dd>
                {colors.typed?.length ? (
                  <div className="swatches">
                    {colors.typed.map((sw) => (
                      <span className="sw" key={sw.hex}>
                        <i style={{ background: sw.hex }} />
                        <span>
                          <Ltr mono>{sw.hex}</Ltr>
                          <div className="muted" style={{ fontSize: 11.5 }}>
                            {sw.type ?? 'untyped'}
                            {sw.hex === colors.primary ? ' · primary' : ''}
                          </div>
                        </span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <Missing />
                )}
              </dd>
              <dt>Typography</dt>
              <dd>
                {fonts.length ? (
                  fonts.map((f) => (
                    <div key={f.name + f.type}>
                      {f.name}{' '}
                      <span className="muted">
                        · {f.type ?? '—'} · {f.origin ?? '—'}
                      </span>
                    </div>
                  ))
                ) : (
                  <Missing />
                )}
              </dd>
            </dl>
          </div>
        </section>
      </div>

      <div className="grid two">
        <section className="card" aria-labelledby="h-domains">
          <div className="card-h">
            <h2 id="h-domains">Domains</h2>
          </div>
          <div className="card-b flush table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th scope="col">Domain</th>
                  <th scope="col">Role</th>
                  <th scope="col">Verification</th>
                  <th scope="col">Source</th>
                </tr>
              </thead>
              <tbody>
                {d.domains.map((x) => (
                  <tr key={x.domain}>
                    <td>
                      <Ltr>{x.domain}</Ltr>
                    </td>
                    <td>{x.role}</td>
                    <td>
                      <Badge
                        tone={
                          x.verification === 'verified'
                            ? 'ok'
                            : x.verification === 'rejected'
                              ? 'bad'
                              : 'warn'
                        }
                      >
                        {x.verification}
                      </Badge>
                    </td>
                    <td className="muted mono">{x.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="card" aria-labelledby="h-aliases">
          <div className="card-h">
            <h2 id="h-aliases">Names & aliases</h2>
          </div>
          <div className="card-b flush table-wrap">
            {d.aliases.length ? (
              <table className="t">
                <thead>
                  <tr>
                    <th scope="col">Alias</th>
                    <th scope="col">Kind</th>
                    <th scope="col">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {d.aliases.map((a) => (
                    <tr key={a.kind + a.normalized}>
                      <td>
                        {a.alias}
                        {a.locale && <span className="muted"> · {a.locale}</span>}
                      </td>
                      <td>{a.kind.replace('_', ' ')}</td>
                      <td className="muted mono" style={{ fontSize: 12 }}>
                        {a.source}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted" style={{ padding: 16, margin: 0 }}>
                No aliases recorded.
              </p>
            )}
          </div>
        </section>
      </div>

      <section className="card" aria-labelledby="h-rel">
        <div className="card-h">
          <h2 id="h-rel">Related entities</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            parent / sub-brand / product relations; only “confirmed” relations are published
          </span>
        </div>
        <div className="card-b flush table-wrap">
          {d.relations.length ? (
            <table className="t">
              <tbody>
                {d.relations.map((r) => (
                  <tr key={r.id}>
                    <td>{r.direction === 'outgoing' ? `${d.entity.canonicalName} → ` : '← '}</td>
                    <td>{r.kind.replace(/_/g, ' ')}</td>
                    <td>
                      <Link className="link" to={`/brands/${r.other.slug}`}>
                        {r.other.name}
                      </Link>
                    </td>
                    <td>
                      <Badge
                        tone={
                          r.status === 'confirmed' ? 'ok' : r.status === 'rejected' ? 'bad' : 'warn'
                        }
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="muted">{r.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted" style={{ padding: 16, margin: 0 }}>
              No parent, sub-brand or related entities recorded.
            </p>
          )}
        </div>
      </section>

      <div className="grid two">
        <section className="card" aria-labelledby="h-cands">
          <div className="card-h">
            <h2 id="h-cands">Source candidates</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              {d.candidates.length} row(s)
            </span>
          </div>
          <div className="card-b flush table-wrap">
            {d.candidates.length ? (
              <table className="t">
                <tbody>
                  {d.candidates.map((x) => (
                    <tr key={x.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{x.name}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          <Ltr mono>
                            {x.sourceFile}#{x.sourceRow}
                          </Ltr>
                        </div>
                      </td>
                      <td>
                        <Ltr>{x.domainOriginal ?? '—'}</Ltr>
                      </td>
                      <td>
                        <StatusBadge kind="candidate" value={x.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted" style={{ padding: 16, margin: 0 }}>
                No candidate row links to this entity (created by historical import or API).
              </p>
            )}
          </div>
        </section>
        <section className="card" aria-labelledby="h-snaps">
          <div className="card-h">
            <h2 id="h-snaps">Source snapshots</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              raw payloads are private; metadata only
            </span>
          </div>
          <div className="card-b flush table-wrap">
            {d.snapshots.length ? (
              <table className="t">
                <tbody>
                  {d.snapshots.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <Badge
                          tone={
                            s.requestKind === 'domain' || s.requestKind === 'webhook'
                              ? 'accent'
                              : 'neutral'
                          }
                        >
                          {s.requestKind}
                        </Badge>
                      </td>
                      <td>
                        <Ltr>{s.requestKey}</Ltr>
                        <div className="faint mono" style={{ fontSize: 11 }}>
                          sha {s.responseSha256.slice(0, 12)} · {fmtBytes(s.byteSize)}
                        </div>
                      </td>
                      <td>
                        {s.outcome}
                        {s.httpStatus ? ` · ${s.httpStatus}` : ''}
                      </td>
                      <td>
                        <Time iso={s.fetchedAt} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted" style={{ padding: 16, margin: 0 }}>
                No source snapshot recorded.
              </p>
            )}
            {d.fetchState.length > 0 && (
              <div style={{ padding: 12, borderTop: '1px solid var(--border)', fontSize: 13 }}>
                {d.fetchState.map((f) => (
                  <div key={f.requestKey}>
                    fetch state <Ltr>{f.requestKey}</Ltr>: <strong>{f.status}</strong> · attempts{' '}
                    {f.attempts}
                    {f.nextAttemptAt && (
                      <>
                        {' '}
                        · next <Time iso={f.nextAttemptAt} relative />
                      </>
                    )}
                    {f.lastError && <div className="err">{f.lastError}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {d.compatRows.length > 0 && (
        <section className="card" aria-labelledby="h-compat">
          <div className="card-h">
            <h2 id="h-compat">api-server compatibility rows</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              brand_intel.catalog_compat — what campaigns read with BRAND_CATALOG_SOURCE=v3
            </span>
          </div>
          <div className="card-b flush table-wrap">
            <table className="t">
              <tbody>
                {d.compatRows.map((r) => (
                  <tr key={r.slug}>
                    <td>
                      <Ltr mono>{r.slug}</Ltr>
                    </td>
                    <td>{r.rowKind}</td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      logo_url {r.logoUrl ? 'set' : '—'} · logo_light_url{' '}
                      {r.logoLightUrl ? 'set' : '—'}
                    </td>
                    <td>
                      <Time iso={r.updatedAt} relative />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {d.content && <Json value={d.content} summary="Raw normalized content (revision payload)" />}
    </div>
  );
}
